import { atom } from "jotai";

import {
	type HttpRequestPage,
	HttpRequestPageSchema,
	getDefaultHttpRequestPage,
} from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { type DocsListItem, docsListItemLocalEntry } from "../document-list";
import { env } from "../env";
import { fs } from "../filesystem";
import { getPageDirPath } from "../paths";
import { HttpRequestLoadError } from "./errors";
import { $loadHttpRequestPage, $setHttpRequestPageError } from "./request-page.atom";

const logger = new Logger("load-http-request-from-disk");

const fsApi = fs();

export interface LoadHttpRequestFromDiskInput {
	key: string;
	document: DocsListItem;
	pageId: string;
	pagePath?: string;
}

/**
 * Loads an HTTP request page from disk and initializes it via $loadHttpRequestPage.
 */
export const $loadHttpRequestFromDisk = atom(
	null,
	async (get, set, input: LoadHttpRequestFromDiskInput) => {
		const { key, document, pageId, pagePath } = input;
		const documentId = document.id;

		try {
			if (!env.isDesktopApp) {
				throw new Error("Disk loading only available in desktop app");
			}

			// Get the document path from the document.
			const documentPath = docsListItemLocalEntry(document)?.path;
			if (!documentPath) {
				throw new Error("Document has no local path");
			}

			// Get the page directory and file path.
			const pageDirPath = await getPageDirPath(documentPath, pageId, pagePath);
			const pageFilePath = await get(fsApi.pathJoin([pageDirPath, "index.yml"]));

			// Check if file exists, create with defaults if not.
			const exists = await get(fsApi.exists({ filePath: pageFilePath }));

			let request: HttpRequestPage;
			if (!exists) {
				logger.info("HTTP request page file not found, creating with defaults", {
					documentId,
					pageId,
					pageFilePath,
				});

				// Create parent directory first
				await set(fsApi.mkdir, { dirPath: pageDirPath, options: { recursive: true } });

				request = getDefaultHttpRequestPage();
				await set(fsApi.writeYamlFile, {
					filePath: pageFilePath,
					content: request,
					documentId,
				});
			} else {
				// Read and parse the YAML file.
				const raw = await get(fsApi.readYamlFile({ filePath: pageFilePath }));

				if (!raw) {
					request = getDefaultHttpRequestPage();
				} else {
					const parsed = HttpRequestPageSchema.safeParse(raw);
					if (!parsed.success) {
						logger.warn("Failed to parse HTTP request page from disk, using defaults", {
							documentId,
							pageId,
							error: parsed.error,
						});
						request = getDefaultHttpRequestPage();
					} else {
						request = parsed.data;
					}
				}
			}

			// Create disk persistence callback.
			const onPersist = async (requestData: HttpRequestPage): Promise<void> => {
				await set(fsApi.writeYamlFile, {
					filePath: pageFilePath,
					content: requestData,
					documentId,
				});
			};

			logger.info("Loading HTTP request page from disk", {
				key,
				documentId,
				pageId,
				pageFilePath,
			});

			// Load the HTTP request page. NEVER SSR, obviously, as this is from disk.
			set($loadHttpRequestPage, {
				key,
				record: {
					source: "disk",
					request,
					importEpoch: 0,
				},
				ssr: false,
				onPersist,
			});
		} catch (err) {
			const error = new HttpRequestLoadError(err);
			logger.error("Failed to load HTTP request page from disk", { documentId, pageId, err });
			set($setHttpRequestPageError, { key, error });
		}
	}
);
$loadHttpRequestFromDisk.debugLabel = "loadHttpRequestFromDisk";
